import { auth } from "../config/firebase";
import { SITE_PAGE_DEFAULTS, SITE_PAGE_KEYS } from "../data/sitePages";
import type { SitePageKey, SitePageToggleMap } from "../types/dataTypes";
import { PUBLIC_API_BASE } from "./publicApi";
import { CategorizedError } from "../types/errors";
import { categorizeError } from "../utils/errorMapper";

const PUBLIC_SITE_SETTINGS_URL = `${PUBLIC_API_BASE}/site-settings/pages`;
const ADMIN_SITE_SETTINGS_URL = `${PUBLIC_API_BASE}/admin/site-settings/pages`;

function mergePageSettings(
  raw: Record<string, unknown> | undefined,
): SitePageToggleMap {
  const merged: SitePageToggleMap = { ...SITE_PAGE_DEFAULTS };
  if (!raw) return merged;
  SITE_PAGE_KEYS.forEach((key) => {
    if (typeof raw[key] === "boolean") {
      merged[key] = Boolean(raw[key]);
    }
  });
  return merged;
}

export async function fetchSitePageSettings(): Promise<SitePageToggleMap> {
  let res: Response;
  try {
    res = await fetch(PUBLIC_SITE_SETTINGS_URL, {
      headers: { Accept: "application/json" },
    });
  } catch (error: any) {
    throw categorizeError(error, "server-error");
  }
  if (!res.ok) {
    if (res.status === 404) {
      console.warn(
        "fetchSitePageSettings: endpoint missing; using default toggles",
      );
      return { ...SITE_PAGE_DEFAULTS };
    }
    throw new CategorizedError(
      `Failed to load site settings (${res.status})`,
      res.status >= 500 ? "server-error" : "unknown",
      res.status,
    );
  }
  const data = await res.json();
  return mergePageSettings(data?.settings);
}

export async function updateSitePageSetting(
  key: SitePageKey,
  enabled: boolean,
): Promise<void> {
  const token = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
  if (!token) throw new Error("Authentication required");

  const res = await fetch(ADMIN_SITE_SETTINGS_URL, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ key, enabled }),
  });

  if (!res.ok) {
    let message = `Failed to update site setting (${res.status})`;
    if (res.status === 404) {
      message =
        "Site settings API not available. Restart or update the admin server.";
    }
    try {
      const payload = await res.json();
      if (payload?.error) message = payload.error;
    } catch {
      // ignore JSON parse errors, keep default message
    }
    throw new Error(message);
  }
}
